import { useLocation, useParams, useSearchParams } from "react-router-dom";

import { useDemoV2 } from "@/features/demo/hooks/useDemoV2";
import { useAuth } from "@/hooks/useAuth";
import { useSession } from "@/lib/hooks/useSession";
import { tenantPath } from "@/lib/urls";
import { useTenantSlug } from "@/stores/tenantStore";

const DEMO_STORAGE_KEYS = {
  slug: "braise:demo_slug_v2",
  jwt: "braise:demo_jwt_v2",
  expiresAt: "braise:demo_expires_at_v2",
};

export const readDemoSessionFromStorage = (expectedSlug: string | null): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    const slug = window.localStorage.getItem(DEMO_STORAGE_KEYS.slug);
    const jwt = window.localStorage.getItem(DEMO_STORAGE_KEYS.jwt);
    const expiresAt = window.localStorage.getItem(DEMO_STORAGE_KEYS.expiresAt);

    if (!slug || !jwt || !expiresAt) {
      return false;
    }

    if (expectedSlug && slug !== expectedSlug) {
      return false;
    }

    const expires = new Date(expiresAt);
    return expires > new Date();
  } catch (error) {
    console.warn("[smart-route] demo storage read failed:", error);
    return false;
  }
};

export interface SmartRouteOptions {
  /** Route key used for tenant redirects, e.g. `dashboard`, `kitchen`. */
  routeKey: string;
  /** Map of legacy absolute paths -> tenant-relative destinations. */
  legacyRedirects?: Record<string, string>;
  /** Optional matcher when the default legacy detection is insufficient. */
  legacyRouteMatch?: (normalizedPath: string) => boolean;
  /** Whether unauthenticated visitors are allowed to see the demo when flagging `mode=demo`. */
  allowDemoWithoutAuth?: boolean;
}

export interface SmartRouteResolution {
  shouldRenderDemo: boolean;
  redirectPath?: string;
  context: {
    subdomain?: string;
    tenantSlug: string | null;
    modeParam: string | null;
    forceDemo: string | null;
    isDemoMode: boolean;
    isForceDemoOverride: boolean;
    hasDemoSession: boolean;
    contextMatches: boolean;
    storageDemoActive: boolean;
    userPresent: boolean;
    serverAuthenticated: boolean;
    isAuthenticated: boolean;
    locationPath: string;
  };
}

export const useSmartRouteResolution = (options: SmartRouteOptions): SmartRouteResolution => {
  const { routeKey, legacyRedirects = {}, legacyRouteMatch, allowDemoWithoutAuth = true } = options;

  const { subdomain } = useParams<{ subdomain: string }>();
  const [searchParams] = useSearchParams();
  const demoContext = useDemoV2();
  const sessionQuery = useSession();
  const { user } = useAuth();
  const location = useLocation();
  const storeTenantSlug = useTenantSlug();
  const tenantSlug = storeTenantSlug ?? subdomain ?? null;

  const isServerAuthenticated = sessionQuery.data?.authenticated ?? false;
  const isAuthenticated = Boolean(user) || isServerAuthenticated;

  const modeParam = searchParams.get("mode");
  const forceDemo = searchParams.get("forceDemo");
  const isDemoMode = modeParam === "demo";

  const contextMatches =
    demoContext.isDemo &&
    !demoContext.isExpired &&
    (!subdomain || demoContext.slug === subdomain);

  const storageDemoActive = !isAuthenticated && readDemoSessionFromStorage(subdomain ?? null);

  const isForceDemoOverride = import.meta.env.MODE !== "production" && isDemoMode && forceDemo === "1";

  const hasDemoSession = contextMatches || storageDemoActive;

  const shouldRenderDemo =
    isDemoMode &&
    (isForceDemoOverride ||
      hasDemoSession ||
      (!isAuthenticated && allowDemoWithoutAuth));

  const normalizedPath = location.pathname.replace(/\/+$/, "");
  let redirectPath: string | undefined;

  if (!shouldRenderDemo && tenantSlug) {
    const legacyRedirectTarget = legacyRedirects[normalizedPath];
    if (legacyRedirectTarget) {
      redirectPath = tenantPath(tenantSlug, legacyRedirectTarget);
    } else {
      const matchesLegacy = legacyRouteMatch
        ? legacyRouteMatch(normalizedPath)
        : (!subdomain && normalizedPath.startsWith(`/${routeKey}`));

      if (matchesLegacy) {
        redirectPath = tenantPath(tenantSlug, routeKey);
      }
    }
  }

  return {
    shouldRenderDemo,
    redirectPath,
    context: {
      subdomain: subdomain ?? undefined,
      tenantSlug,
      modeParam,
      forceDemo,
      isDemoMode,
      isForceDemoOverride,
      hasDemoSession,
      contextMatches,
      storageDemoActive,
      userPresent: Boolean(user),
      serverAuthenticated: isServerAuthenticated,
      isAuthenticated,
      locationPath: location.pathname,
    },
  };
};
